import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import axios from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const BuyerPhoneVerificationPage = () => {
  const navigate = useNavigate();
  const { refreshCurrentUser } = useAuth();

  const [phone, setPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const loadStatus = async () => {
      try {
        setLoadingStatus(true);
        setError('');
        const response = await axios.get('/api/phone-verification/status');
        const status = response.data || {};
        const countryCode = status.country_isd_code || '';
        const number = status.phone_number || '';
        setPhone(`${countryCode} ${number}`.trim());
        setPhoneVerified(Boolean(status.phone_verified));

        if (status.phone_verified) {
          await refreshCurrentUser();
          navigate('/buyer/profile');
        } else {
          // Auto-send OTP when the page opens to reduce friction.
          try {
            const otpResponse = await axios.post('/api/phone-verification/send-otp');
            setInfo(otpResponse.data?.message || 'OTP sent on WhatsApp.');
          } catch (otpError) {
            setError(otpError.response?.data?.detail || 'Failed to send OTP automatically. Please use Send OTP.');
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load phone verification status');
      } finally {
        setLoadingStatus(false);
      }
    };

    loadStatus();
  }, [navigate, refreshCurrentUser]);

  const sendOtp = async () => {
    try {
      setSendingOtp(true);
      setError('');
      const response = await axios.post('/api/phone-verification/send-otp');
      setInfo(response.data?.message || 'OTP sent on WhatsApp.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp.trim())) {
      setError('Enter a valid 6-digit OTP.');
      return;
    }

    try {
      setVerifyingOtp(true);
      setError('');
      await axios.post('/api/phone-verification/verify-otp', { otp: otp.trim() });
      setInfo('Phone verified successfully. Redirecting...');
      await refreshCurrentUser();
      navigate('/buyer/profile');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (loadingStatus) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading verification status...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={7} lg={5}>
          <Card>
            <Card.Header className="text-center">
              <h4 className="mb-0">Verify Your Phone</h4>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-2">WhatsApp OTP will be sent to your registered phone number.</p>
              <p className="text-muted mb-2"><strong>{phone || '-'}</strong></p>
              {phoneVerified ? (
                <Alert variant="success" className="mb-0">Your phone is already verified.</Alert>
              ) : (
                <>
                  {info && <Alert variant="info">{info}</Alert>}
                  {error && <Alert variant="danger">{error}</Alert>}

                  <Form.Group className="mb-3">
                    <Form.Label>Enter OTP</Form.Label>
                    <Form.Control
                      type="text"
                      value={otp}
                      onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={sendOtp} disabled={sendingOtp || verifyingOtp}>
                      {sendingOtp ? 'Sending...' : 'Send OTP'}
                    </Button>
                    <Button variant="primary" onClick={verifyOtp} disabled={sendingOtp || verifyingOtp}>
                      {verifyingOtp ? 'Verifying...' : 'Verify Phone'}
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BuyerPhoneVerificationPage;
